import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV, CONTACT, SERVICE_GROUPS } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setOpen(false);
    if (item.route) {
      navigate(item.href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + item.href);
    } else {
      document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const requestService = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/#request");
    else document.querySelector("#request")?.scrollIntoView({ behavior: "smooth" });
  };

  const openServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const closeServicesDelayed = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 120);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled
          ? "bg-mm-base/95 backdrop-blur-sm border-b border-mm-green/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src="/assets/logo.png" alt="Mobile Maintenance" className="h-9 sm:h-10 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => {
            const isFleetServices = item.href === "#services";
            if (isFleetServices) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={openServices}
                  onMouseLeave={closeServicesDelayed}
                >
                  <button
                    data-testid="nav-services-link"
                    onClick={() => go(item)}
                    className={`inline-flex items-center gap-1.5 font-mono text-[12px] tracking-[0.15em] uppercase transition-colors duration-200 ${
                      servicesOpen ? "text-mm-green" : "text-gray-300 hover:text-mm-green"
                    }`}
                  >
                    {item.label}
                    <ChevronDown
                      size={12}
                      className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>
              );
            }
            return (
              <button
                key={item.label}
                data-testid={`nav-${item.href.replace(/[#/]/g, "")}-link`}
                onClick={() => go(item)}
                className="font-mono text-[12px] tracking-[0.15em] uppercase text-gray-300 hover:text-mm-green transition-colors duration-200"
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-phone"
            className="hidden md:flex items-center gap-2 font-mono text-sm text-white hover:text-mm-green transition-colors"
          >
            <Phone size={15} className="text-mm-green" /> {CONTACT.phone}
          </a>
          <button
            onClick={requestService}
            data-testid="nav-get-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 font-heading font-bold uppercase tracking-wider text-sm bg-mm-green text-black px-5 py-2.5 hover:bg-white hover:shadow-[0_0_20px_rgba(0,177,64,0.6)] transition-all duration-200 shadow-[0_0_0_rgba(0,177,64,0)]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
            Get A Quote
          </button>
          <button
            className="lg:hidden text-white p-1"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Services Mega-Menu (desktop) */}
      <AnimatePresence>
        {servicesOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute top-full left-0 right-0 bg-mm-base backdrop-blur-md border-t border-b border-mm-green/25 shadow-2xl"
            style={{ backgroundColor: "#050505" }}
            onMouseEnter={openServices}
            onMouseLeave={closeServicesDelayed}
            data-testid="services-mega-menu"
          >
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10">
              <div className="grid grid-cols-6 gap-6">
                {SERVICE_GROUPS.map((group, gi) => (
                  <div key={group.category}>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-mm-green mb-3">
                      {String(gi + 1).padStart(2, "0")}
                    </div>
                    <div className="font-heading font-black uppercase text-white text-lg tracking-tight leading-tight mb-4 pb-3 border-b border-mm-green/20">
                      {group.category}
                    </div>
                    <ul className="space-y-2">
                      {group.items.map((s) => (
                        <li key={s.slug}>
                          <Link
                            to={`/services/${s.slug}`}
                            onClick={() => setServicesOpen(false)}
                            data-testid={`mega-link-${s.slug}`}
                            className="group inline-flex items-start gap-1.5 text-sm text-gray-400 hover:text-mm-green transition-colors leading-snug"
                          >
                            <span>{s.title}</span>
                            <ArrowUpRight
                              size={12}
                              className="opacity-0 group-hover:opacity-100 -translate-y-0.5 transition-opacity mt-0.5 shrink-0"
                            />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-mm-green/15 flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-500">
                  {SERVICE_GROUPS.reduce((a, g) => a + g.items.length, 0)} Total Services
                </span>
                <button
                  onClick={() => {
                    setServicesOpen(false);
                    requestService();
                  }}
                  data-testid="mega-cta-quote"
                  className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-mm-green hover:text-white transition-colors"
                >
                  Get A Quote <ArrowUpRight size={12} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-mm-base border-t border-mm-green/15"
          >
            <div className="px-5 py-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto">
              {NAV.map((item) => {
                const isFleetServices = item.href === "#services";
                if (isFleetServices) {
                  return (
                    <div key={item.label} className="border-b border-mm-green/15 pb-3">
                      <button
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        className="w-full flex items-center justify-between text-left font-heading text-2xl uppercase text-white"
                        data-testid="mobile-nav-services-toggle"
                      >
                        {item.label}
                        <ChevronDown
                          size={20}
                          className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-3 pl-2 space-y-4">
                              {SERVICE_GROUPS.map((g) => (
                                <div key={g.category}>
                                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-mm-green mb-2">
                                    {g.category}
                                  </div>
                                  <ul className="space-y-1.5">
                                    {g.items.map((s) => (
                                      <li key={s.slug}>
                                        <Link
                                          to={`/services/${s.slug}`}
                                          onClick={() => setOpen(false)}
                                          className="text-gray-300 text-base hover:text-mm-green"
                                        >
                                          {s.title}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <button
                    key={item.label}
                    onClick={() => go(item)}
                    className="text-left font-heading text-2xl uppercase text-white hover:text-mm-green transition-colors"
                  >
                    {item.label}
                  </button>
                );
              })}
              <button
                onClick={requestService}
                data-testid="nav-mobile-get-quote-btn"
                className="mt-2 font-heading font-bold uppercase bg-mm-green text-black px-5 py-3 text-lg"
              >
                Get A Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
